import {Button} from "primereact/button";
import {InputTextarea} from "primereact/inputtextarea";
import type {FormEvent, KeyboardEvent} from "react";
import {useEffect, useRef, useState} from "react";

import {ChatMarkdown} from "../component/ChatMarkdown";
import {computeErrorText} from "../component/I18nErrorMessage";
import {MainFrame, MainToolbar} from "../component/MainFrame";
import {useRevaTranslation} from "../hooks/languageHooks";
import {ErrorI18n} from "../i18n";
import {sendChatMessageThunk} from "../redux/chatThunk";
import {useRevaDispatch} from "../redux/revaHooks";

type ChatAuthor = "user" | "assistant";

interface ChatMessage {
    id: number;
    author: ChatAuthor;
    text: string;
    /** Antwort der KI steht noch aus, die Blase zeigt statt Text einen Spinner. */
    pending?: boolean;
    /** Die Anfrage an die KI ist fehlgeschlagen, `text` enthält die Fehlermeldung. */
    failed?: boolean;
    /** Die KI hat am Längenlimit abgebrochen, hinter `text` steht ein Hinweis darauf. */
    truncated?: boolean;
}

/** Antworten der KI kommen als Markdown; Eingaben des Nutzers und Fehlermeldungen erscheinen wörtlich. */
function isMarkdown(message: ChatMessage): boolean {
    return message.author === "assistant" && !message.failed;
}

/**
 * Chat mit der KI. Jede Nachricht geht über {@link sendChatMessageThunk} an die KI; bis die Antwort da ist, steht
 * an ihrer Stelle eine leere Blase mit Spinner. Der Verlauf lebt nur im Komponenten-State und geht beim Verlassen
 * der Seite verloren. Antworten der KI werden als Markdown (GFM) gerendert, Nutzer-Nachrichten und Fehlermeldungen
 * als Klartext.
 *
 * Enter fügt im Eingabefeld eine neue Zeile ein, Ctrl+Enter (bzw. Cmd+Enter) schickt die Nachricht ab.
 */
function PageChat() {
    const {t, language} = useRevaTranslation();
    const dispatch = useRevaDispatch();
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [draft, setDraft] = useState("");
    const nextId = useRef(0);
    const endRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // scrollIntoView fehlt in jsdom.
        endRef.current?.scrollIntoView?.({block: "end"});
    }, [messages]);

    const send = async () => {
        const text = draft.trim();
        if (!text) {
            return;
        }
        const userId = nextId.current++;
        const answerId = nextId.current++;
        setMessages((prev) => [
            ...prev,
            {id: userId, author: "user", text},
            {id: answerId, author: "assistant", text: "", pending: true},
        ]);
        setDraft("");

        let answer: Pick<ChatMessage, "text" | "failed" | "truncated">;
        try {
            const response = await dispatch(sendChatMessageThunk(text));
            answer = {text: response.answer, truncated: response.truncated};
        } catch (e) {
            console.log("Chat request failed", e);
            // Qualified-Errors von reva-rest tragen den konkreten Grund (z.B. "KI-Dienst nicht erreichbar").
            const errorText =
                e instanceof ErrorI18n
                    ? computeErrorText(t, language, {i18nKey: e.message, i18nArgs: e.messageArgs})
                    : t("chat.error");
            answer = {text: errorText, failed: true};
        }
        setMessages((prev) => prev.map((m) => (m.id === answerId ? {...m, ...answer, pending: false} : m)));
    };

    const onSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        void send();
    };

    const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
            event.preventDefault();
            void send();
        }
    };

    return (
        <MainFrame>
            <MainToolbar title={t("chat.title")} />
            <div className="flex flex-col grow gap-3 pb-4" role="log" aria-label={t("chat.title")}>
                {messages.length === 0 && (
                    <div className="m-auto text-surface-500 dark:text-surface-400">{t("chat.empty")}</div>
                )}
                {messages.map((message) => (
                    <div
                        key={message.id}
                        data-author={message.author}
                        data-pending={message.pending || undefined}
                        aria-busy={message.pending || undefined}
                        className={
                            "max-w-[66.666667%] px-4 py-2 rounded-lg break-words " +
                            (isMarkdown(message) ? "" : "whitespace-pre-wrap ") +
                            (message.author === "user"
                                ? "self-end bg-primary text-primary-contrast"
                                : "self-start bg-surface-100 dark:bg-surface-800") +
                            (message.failed ? " text-red-600 dark:text-red-400" : "")
                        }
                    >
                        {message.pending ? (
                            <i className="pi pi-spin pi-spinner" role="status" aria-label={t("chat.pending")} />
                        ) : isMarkdown(message) ? (
                            <ChatMarkdown text={message.text}>
                                {message.truncated && (
                                    <p className="my-2 text-surface-500 dark:text-surface-400">
                                        {" "}
                                        {t("chat.truncated")}
                                    </p>
                                )}
                            </ChatMarkdown>
                        ) : (
                            message.text
                        )}
                    </div>
                ))}
                <div ref={endRef} />
            </div>
            <form
                onSubmit={onSubmit}
                className="sticky bottom-0 flex gap-2 items-end p-3 border-t border-surface-200 dark:border-surface-700 bg-surface-0 dark:bg-surface-900"
            >
                <InputTextarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={onKeyDown}
                    rows={3}
                    autoResize
                    autoFocus
                    placeholder={t("chat.placeholder")}
                    aria-label={t("chat.placeholder")}
                    className="grow max-h-60 !overflow-y-auto"
                />
                <Button
                    type="submit"
                    icon="pi pi-send"
                    label={t("chat.send")}
                    disabled={!draft.trim()}
                    tooltip={t("chat.sendHint")}
                    tooltipOptions={{position: "top"}}
                />
            </form>
        </MainFrame>
    );
}

export default PageChat;
